import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Building2, Trees, Wrench, Eye, ClipboardList, Users, Car, Ship } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const servicesData = {
  EN: [
    {
      id: "property-management",
      icon: Building2,
      title: "Private Property Management",
      intro: "Overall management and coordination of your Mallorca property while you are away, ensuring everything runs smoothly year-round.",
      whatWeDo: [
        "Overall property oversight while the owner is away",
        "Coordination of household service providers",
        "Contractor coordination when required",
        "Utilities and bills administration",
        "Pre-arrival preparation and readiness checks",
        "Guest or tenant support when needed",
        "Incident handling and emergency coordination",
        "Key holding and access management"
      ],
      typicalRequests: [
        "Manage the property while I'm abroad for six months",
        "Coordinate the cleaning team before my arrival",
        "Prepare the house before my family arrives next week",
        "Handle the insurance claim for the water damage",
        "Manage access for service providers"
      ],
      image: "https://customer-assets.emergentagent.com/job_cd17f3e5-2b43-4862-92f7-9dbae9dcf0a1/artifacts/v86m4x0c_ChatGPT%20Image%205%20mar%202026%2C%2013_59_52.png"
    },
    {
      id: "garden-pool",
      icon: Trees,
      title: "Garden & Pool",
      intro: "Professional coordination and oversight of your outdoor spaces, maintaining the beauty and functionality of your gardens and pool areas through local specialists.",
      whatWeDo: [
        "Weekly pool checks coordination",
        "Water testing and chemistry management supervision",
        "Pool equipment checks and maintenance coordination",
        "Coordination of garden and landscaping maintenance",
        "Irrigation system monitoring and adjustments",
        "Seasonal preparation (winterization/opening)",
        "Supplier coordination and quality control",
        "Equipment replacement recommendations"
      ],
      typicalRequests: [
        "The pool pump is making a strange noise—can you check it?",
        "Coordinate the gardener for the hedge trimming",
        "Prepare the pool for opening before the summer season",
        "The irrigation system needs adjustment for the dry season",
        "Get quotes for replacing the pool cover"
      ],
      image: "https://customer-assets.emergentagent.com/job_cd17f3e5-2b43-4862-92f7-9dbae9dcf0a1/artifacts/3051h5lg_thanassis-lemo-1IMf5uf4dSw-unsplash.jpg"
    },
    {
      id: "renovations",
      icon: Wrench,
      title: "Renovations & Technical Works",
      clarifier: "For projects requiring planning, budget management and on-site supervision.",
      intro: "Expert coordination of renovation projects and technical upgrades, ensuring quality execution and minimal disruption through hands-on supervision.",
      whatWeDo: [
        "Project scoping and contractor tendering",
        "Planning and timeline management",
        "On-site supervision during works",
        "Quality control and standards verification",
        "Regular progress photo updates",
        "Budget monitoring and cost control",
        "Handover and final inspection",
        "Warranty and defect follow-up"
      ],
      typicalRequests: [
        "Get quotes for a bathroom renovation",
        "Supervise the kitchen remodel while I'm in London",
        "Coordinate the new air conditioning installation",
        "The terrace needs waterproofing—manage the project",
        "Weekly photo updates on the renovation progress"
      ],
      image: "https://customer-assets.emergentagent.com/job_cd17f3e5-2b43-4862-92f7-9dbae9dcf0a1/artifacts/zvcgcofy_dominik-8AZ_2om65TU-unsplash.jpg"
    },
    {
      id: "property-supervision",
      icon: Eye,
      title: "Property Oversight",
      clarifier: "A standalone inspection service for owners who want regular on-site visits and detailed reports without full property management.",
      intro: "Regular on-site visits and inspections to ensure your property remains secure and in good condition. We provide detailed reports and identify issues before they escalate.",
      whatWeDo: [
        "Regular on-site property visits",
        "Detailed condition checks of the house and exterior areas",
        "Photo reports after each visit",
        "Storm and weather inspections",
        "Mail collection and management",
        "Vacancy security checks",
        "Utility readings and monitoring",
        "Issue identification and reporting to the owner"
      ],
      typicalRequests: [
        "Visit the property twice a month and send me a report",
        "Check the house after the storm last night",
        "Collect the mail and scan anything important",
        "Make sure all windows and doors are secure",
        "Send photos of the garden and pool condition"
      ],
      image: "https://customer-assets.emergentagent.com/job_cd17f3e5-2b43-4862-92f7-9dbae9dcf0a1/artifacts/e8m4ukve_pmaU5or_3kauMl23qYFpoDhAq6uKCqxvc8m87V1HyHrqQHRhsJK_dWrPsIEuCrTpZannWGK3P4Rcctc-DLYu3WH7JnLk00fcTPKqqlZ1YOM.jpeg"
    },
    {
      id: "maintenance",
      icon: ClipboardList,
      title: "Technical Maintenance Coordination",
      clarifier: "For day-to-day repairs, breakdowns and recurring maintenance needs.",
      intro: "Coordination of technical repairs and maintenance through trusted local contractors. We manage technicians, obtain quotes, schedule works and ensure quality delivery.",
      whatWeDo: [
        "Management of local contractors and technicians",
        "Quote comparison and contractor recommendation",
        "Scheduling of technical repairs and maintenance works",
        "On-site presence during technical visits when required",
        "Work quality verification and follow-up",
        "Invoice review and payment coordination",
        "Maintenance history tracking and documentation",
        "Coordination of cleaning or housekeeping services when required"
      ],
      typicalRequests: [
        "The air conditioning isn't cooling properly—arrange a technician",
        "Get three quotes for repainting the exterior",
        "Schedule the annual boiler service",
        "The gate motor is broken—coordinate the repair",
        "Be present when the plumber arrives on Thursday"
      ],
      image: "https://static.prod-images.emergentagent.com/jobs/cd17f3e5-2b43-4862-92f7-9dbae9dcf0a1/images/f8bb7861769361b348736df92ce87ca886f36d56f1f1686e1bc11fcbc208a8ed.png"
    },
    {
      id: "concierge",
      icon: Users,
      title: "Concierge Services",
      intro: "Personalized assistance for your arrivals and stays in Mallorca, ensuring everything is prepared and arranged through our network of trusted partners.",
      whatWeDo: [
        "Pre-arrival readiness preparation",
        "Grocery provisioning and stocking",
        "Household and lifestyle arrangement coordination",
        "Trusted partner referrals (transfers, reservations)",
        "Welcome pack preparation and property airing before arrival.",
        "Local recommendations",
        "Special requests handling",
        "Departure coordination"
      ],
      typicalRequests: [
        "Stock the fridge before we arrive on Friday",
        "Arrange airport transfer for the family",
        "Recommend a good restaurant for 8 people",
        "Have the house aired and ready by noon",
        "Coordinate a private chef for Saturday dinner"
      ],
      image: "https://customer-assets.emergentagent.com/job_cd17f3e5-2b43-4862-92f7-9dbae9dcf0a1/artifacts/1k5jyb7j_public.jpeg"
    },
    {
      id: "vehicle",
      icon: Car,
      title: "Vehicle Management",
      intro: "Comprehensive oversight of your vehicles stored in Mallorca, ensuring they remain maintained and ready for your arrival.",
      whatWeDo: [
        "Battery checks and maintenance",
        "ITV (MOT) coordination and scheduling",
        "Regular cleaning coordination",
        "Storage facility coordination",
        "Airport-ready preparation",
        "Insurance and documentation tracking",
        "Mechanical service coordination",
        "Fuel and fluid level checks"
      ],
      typicalRequests: [
        "Start the car weekly while I'm away",
        "The ITV is due next month—handle it",
        "Have the car cleaned and fueled before my arrival",
        "Coordinate the annual service at the dealership",
        "Check the tire pressure and battery"
      ],
      image: "https://customer-assets.emergentagent.com/job_cd17f3e5-2b43-4862-92f7-9dbae9dcf0a1/artifacts/ljqoiz21_lucas-clarysse-4xi6Q0gE_eU-unsplash.jpg"
    },
    {
      id: "yacht",
      icon: Ship,
      title: "Yacht Maintenance Coordination",
      intro: "Dedicated coordination of maintenance, yard services and pre-season preparation for your yacht in Mallorca, ensuring schedules are met and suppliers deliver quality work.",
      whatWeDo: [
        "Yard and shipyard task coordination",
        "Technical supplier management",
        "Maintenance schedule oversight",
        "Checklist verification and tracking",
        "Progress reporting and documentation",
        "Quality inspection coordination",
        "Supplier liaison and communication",
        "Pre-season preparation oversight"
      ],
      typicalRequests: [
        "Coordinate the hull cleaning before the season",
        "Manage the engine service with the yard",
        "Get quotes for the teak deck restoration",
        "Ensure the antifouling is done by April",
        "Track progress on the electronics upgrade"
      ],
      image: "https://customer-assets.emergentagent.com/job_cd17f3e5-2b43-4862-92f7-9dbae9dcf0a1/artifacts/bjalgy7e_monika-guzikowska-UXmuQ6Ag_SE-unsplash.jpg"
    }
  ],
  ES: [
    {
      id: "property-management",
      icon: Building2,
      title: "Gestión Privada de Propiedades",
      intro: "Gestión integral y coordinación de su propiedad en Mallorca mientras está fuera. Nos aseguramos de que su hogar permanezca listo, protegido y bien coordinado durante todo el año.",
      whatWeDo: [
        "Coordinación general de la propiedad mientras el propietario está fuera",
        "Coordinación de proveedores de servicios del hogar",
        "Coordinación de contratistas cuando sea necesario",
        "Administración de servicios y facturas",
        "Preparación previa a la llegada y verificaciones de disponibilidad",
        "Soporte para huéspedes o inquilinos cuando sea necesario",
        "Gestión de incidencias y coordinación de emergencias",
        "Custodia de llaves y gestión de accesos"
      ],
      typicalRequests: [
        "Gestionar la propiedad mientras estoy fuera durante seis meses",
        "Coordinar al equipo de limpieza antes de mi llegada",
        "Preparar la casa antes de que llegue mi familia la próxima semana",
        "Gestionar la reclamación del seguro por daños por agua",
        "Gestionar el acceso para los proveedores de servicios"
      ],
      image: "https://customer-assets.emergentagent.com/job_cd17f3e5-2b43-4862-92f7-9dbae9dcf0a1/artifacts/v86m4x0c_ChatGPT%20Image%205%20mar%202026%2C%2013_59_52.png"
    },
    {
      id: "garden-pool",
      icon: Trees,
      title: "Jardín y Piscina",
      intro: "Supervisión profesional y coordinación de sus espacios exteriores, manteniendo la belleza y funcionalidad de sus jardines y áreas de piscina a través de especialistas locales.",
      whatWeDo: [
        "Coordinación de controles semanales de piscina",
        "Supervisión de análisis de agua y gestión química",
        "Controles de equipos de piscina y coordinación de mantenimiento",
        "Coordinación de mantenimiento de jardín y paisajismo",
        "Monitorización y ajustes del sistema de riego",
        "Preparación estacional (invernización/apertura)",
        "Coordinación de proveedores y control de calidad",
        "Recomendaciones de reemplazo de equipos"
      ],
      typicalRequests: [
        "La bomba de la piscina hace un ruido extraño—¿pueden revisarlo?",
        "Coordinar al jardinero para el recorte de setos",
        "Preparar la piscina para la apertura antes del verano",
        "El sistema de riego necesita ajuste para la temporada seca",
        "Obtener presupuestos para reemplazar la cubierta de la piscina"
      ],
      image: "https://customer-assets.emergentagent.com/job_cd17f3e5-2b43-4862-92f7-9dbae9dcf0a1/artifacts/3051h5lg_thanassis-lemo-1IMf5uf4dSw-unsplash.jpg"
    },
    {
      id: "renovations",
      icon: Wrench,
      title: "Reformas y Trabajos Técnicos",
      clarifier: "Para proyectos que requieren planificación, gestión de presupuesto y supervisión in situ.",
      intro: "Coordinación experta de proyectos de reforma y mejoras técnicas, asegurando ejecución de calidad y mínima interrupción mediante supervisión directa.",
      whatWeDo: [
        "Definición del proyecto y solicitud de presupuestos",
        "Planificación y gestión de plazos",
        "Supervisión in situ durante las obras",
        "Control de calidad y verificación de estándares",
        "Actualizaciones fotográficas regulares del progreso",
        "Monitorización del presupuesto y control de costes",
        "Entrega e inspección final",
        "Seguimiento de garantías y defectos"
      ],
      typicalRequests: [
        "Obtener presupuestos para una reforma de baño",
        "Supervisar la reforma de la cocina mientras estoy en Londres",
        "Coordinar la instalación del nuevo aire acondicionado",
        "La terraza necesita impermeabilización—gestionar el proyecto",
        "Actualizaciones fotográficas semanales del progreso de la reforma"
      ],
      image: "https://customer-assets.emergentagent.com/job_cd17f3e5-2b43-4862-92f7-9dbae9dcf0a1/artifacts/zvcgcofy_dominik-8AZ_2om65TU-unsplash.jpg"
    },
    {
      id: "property-supervision",
      icon: Eye,
      title: "Supervisión de Propiedades",
      clarifier: "Un servicio de inspección independiente para propietarios que desean visitas presenciales regulares e informes detallados sin una gestión integral de la propiedad.",
      intro: "Visitas presenciales regulares e inspecciones para asegurar que su propiedad permanezca segura y en buen estado. Proporcionamos informes detallados e identificamos problemas antes de que escalen.",
      whatWeDo: [
        "Visitas presenciales regulares a la propiedad",
        "Comprobaciones detalladas del estado de la casa y áreas exteriores",
        "Informes fotográficos después de cada visita",
        "Inspecciones tras tormentas y condiciones climáticas",
        "Recogida y gestión de correo",
        "Controles de seguridad durante vacancia",
        "Lecturas y monitorización de servicios",
        "Identificación de problemas e informe al propietario"
      ],
      typicalRequests: [
        "Visitar la propiedad dos veces al mes y enviarme un informe",
        "Comprobar la casa después de la tormenta de anoche",
        "Recoger el correo y escanear lo importante",
        "Asegurar que todas las ventanas y puertas estén seguras",
        "Enviar fotos del estado del jardín y la piscina"
      ],
      image: "https://customer-assets.emergentagent.com/job_cd17f3e5-2b43-4862-92f7-9dbae9dcf0a1/artifacts/e8m4ukve_pmaU5or_3kauMl23qYFpoDhAq6uKCqxvc8m87V1HyHrqQHRhsJK_dWrPsIEuCrTpZannWGK3P4Rcctc-DLYu3WH7JnLk00fcTPKqqlZ1YOM.jpeg"
    },
    {
      id: "maintenance",
      icon: ClipboardList,
      title: "Coordinación de Mantenimiento Técnico",
      clarifier: "Para reparaciones del día a día, averías y necesidades de mantenimiento recurrentes.",
      intro: "Coordinación de reparaciones técnicas y mantenimiento a través de contratistas locales de confianza. Gestionamos técnicos, obtenemos presupuestos, programamos trabajos y aseguramos una entrega de calidad.",
      whatWeDo: [
        "Gestión de contratistas y técnicos locales",
        "Comparación de presupuestos y recomendación de contratistas",
        "Programación de reparaciones técnicas y trabajos de mantenimiento",
        "Presencia in situ durante visitas técnicas cuando sea necesario",
        "Verificación de calidad del trabajo y seguimiento",
        "Revisión de facturas y coordinación de pagos",
        "Seguimiento y documentación del historial de mantenimiento",
        "Coordinación de servicios de limpieza doméstica cuando sea necesario"
      ],
      typicalRequests: [
        "El aire acondicionado no enfría bien—organizar un técnico",
        "Obtener tres presupuestos para repintar el exterior",
        "Programar el servicio anual de la caldera",
        "El motor de la puerta está roto—coordinar la reparación",
        "Estar presente cuando llegue el fontanero el jueves"
      ],
      image: "https://static.prod-images.emergentagent.com/jobs/cd17f3e5-2b43-4862-92f7-9dbae9dcf0a1/images/f8bb7861769361b348736df92ce87ca886f36d56f1f1686e1bc11fcbc208a8ed.png"
    },
    {
      id: "concierge",
      icon: Users,
      title: "Servicios de Conserjería",
      intro: "Asistencia personalizada para sus llegadas y estancias, asegurando que todo esté preparado y organizado a través de nuestra red de socios de confianza.",
      whatWeDo: [
        "Preparación de la propiedad antes de la llegada",
        "Aprovisionamiento de comestibles",
        "Coordinación de gestiones del hogar y estilo de vida",
        "Referencias de socios de confianza (traslados, reservas)",
        "Preparación del pack de bienvenida y ventilación de la propiedad antes de la llegada.",
        "Recomendaciones locales",
        "Gestión de solicitudes especiales",
        "Coordinación de salida"
      ],
      typicalRequests: [
        "Abastecer la nevera antes de que lleguemos el viernes",
        "Organizar el traslado desde el aeropuerto para la familia",
        "Recomendar un buen restaurante para 8 personas",
        "Tener la casa ventilada y lista al mediodía",
        "Coordinar un chef privado para la cena del sábado"
      ],
      image: "https://customer-assets.emergentagent.com/job_cd17f3e5-2b43-4862-92f7-9dbae9dcf0a1/artifacts/1k5jyb7j_public.jpeg"
    },
    {
      id: "vehicle",
      icon: Car,
      title: "Gestión de Vehículos",
      intro: "Supervisión integral de sus vehículos almacenados en Mallorca, asegurando que permanezcan mantenidos y listos para su llegada.",
      whatWeDo: [
        "Controles y mantenimiento de batería",
        "Coordinación y programación de ITV",
        "Coordinación de limpieza regular",
        "Coordinación de instalaciones de almacenamiento",
        "Preparación lista para aeropuerto",
        "Seguimiento de seguros y documentación",
        "Coordinación de servicio mecánico",
        "Controles de niveles de combustible y fluidos"
      ],
      typicalRequests: [
        "Arrancar el coche semanalmente mientras estoy fuera",
        "La ITV vence el próximo mes—gestionarlo",
        "Tener el coche limpio y con combustible antes de mi llegada",
        "Coordinar el servicio anual en el concesionario",
        "Comprobar la presión de los neumáticos y la batería"
      ],
      image: "https://customer-assets.emergentagent.com/job_cd17f3e5-2b43-4862-92f7-9dbae9dcf0a1/artifacts/ljqoiz21_lucas-clarysse-4xi6Q0gE_eU-unsplash.jpg"
    },
    {
      id: "yacht",
      icon: Ship,
      title: "Coordinación de Mantenimiento de Yates",
      intro: "Coordinación dedicada del mantenimiento, servicios de varadero y preparación de pretemporada para su yate en Mallorca, asegurando que se cumplan los plazos y los proveedores entreguen trabajo de calidad.",
      whatWeDo: [
        "Coordinación de tareas de varadero y astillero",
        "Gestión de proveedores técnicos",
        "Supervisión del calendario de mantenimiento",
        "Verificación y seguimiento de checklists",
        "Informes de progreso y documentación",
        "Coordinación de inspecciones de calidad",
        "Enlace y comunicación con proveedores",
        "Supervisión de preparación de pretemporada"
      ],
      typicalRequests: [
        "Coordinar la limpieza del casco antes de la temporada",
        "Gestionar el servicio del motor con el varadero",
        "Obtener presupuestos para la restauración de la cubierta de teca",
        "Asegurar que el antifouling esté hecho en abril",
        "Seguir el progreso de la actualización de la electrónica"
      ],
      image: "https://customer-assets.emergentagent.com/job_cd17f3e5-2b43-4862-92f7-9dbae9dcf0a1/artifacts/bjalgy7e_monika-guzikowska-UXmuQ6Ag_SE-unsplash.jpg"
    }
  ]
};

const ServicesPage = () => {
  const location = useLocation();
  const { language, t } = useLanguage();
  const services = servicesData[language];

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div data-testid="services-page">
      <Helmet>
        <title>Property Management Services Mallorca | Balearic Home Solutions</title>
        <meta name="description" content="Comprehensive property management services in Mallorca — maintenance coordination, renovations, garden & pool, concierge and more. Southwest Mallorca specialists." />
        <link rel="canonical" href="https://balearichomesolutions.com/services" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-500 mb-4">
              {t.servicesPage.label}
            </p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-navy tracking-tight mb-6">
              {t.servicesPage.title}
            </h1>
            <p className="text-stone-600 text-lg leading-relaxed">
              {t.servicesPage.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-24 md:space-y-32">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                id={service.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ delay: 0.1 }}
                data-testid={`service-section-${service.id}`}
                className="scroll-mt-24"
              >
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start ${
                  index % 2 === 1 ? 'lg:grid-flow-dense' : ''
                }`}>
                  {/* Image */}
                  <div className={`img-zoom ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                    <img
                      src={service.image}
                      alt={service.title}
                      className={`w-full h-auto aspect-[4/3] object-cover ${
                        service.id === 'property-management' ? 'object-[center_65%]' : ''
                      }${
                        service.id === 'garden-pool' ? 'object-[center_70%]' : ''
                      }${
                        service.id === 'property-supervision' ? 'object-[center_85%]' : ''
                      }${
                        service.id === 'concierge' ? 'object-[center_60%]' : ''
                      }${
                        service.id === 'yacht' ? 'object-[center_55%]' : ''
                      }${
                        service.id === 'renovations' ? 'object-[center_65%]' : ''
                      }${
                        service.id === 'vehicle' ? 'object-[35%_center]' : ''
                      }${
                        service.id === 'maintenance' ? 'object-[center_50%]' : ''
                      }`}
                    />
                  </div>

                  {/* Content */}
                  <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                    <div className="flex items-center gap-3 mb-4">
                      <service.icon size={24} className="text-navy" strokeWidth={1.5} />
                      <span className="text-xs font-bold tracking-widest uppercase text-stone-500">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                    
                    <h2 className="font-heading text-3xl md:text-4xl text-navy tracking-tight mb-4">
                      {service.title}
                    </h2>
                    
                    {service.clarifier && (
                      <p className="text-navy font-medium leading-relaxed mb-3">
                        {service.clarifier}
                      </p>
                    )}
                    <p className="text-stone-600 leading-relaxed mb-8">
                      {service.intro}
                    </p>

                    {/* What We Do */}
                    <div className="mb-8">
                      <h3 className="text-xs font-bold tracking-widest uppercase text-navy mb-4">
                        {t.servicesPage.whatWeDo}
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {service.whatWeDo.map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <span className="w-1 h-1 bg-navy rounded-full mt-2 flex-shrink-0" />
                            <span className="text-sm text-stone-600">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Typical Requests */}
                    <div className="mb-8 bg-stone-50 p-6 border-l-2 border-navy">
                      <h3 className="text-xs font-bold tracking-widest uppercase text-navy mb-4">
                        {t.servicesPage.typicalRequests}
                      </h3>
                      <div className="space-y-2">
                        {service.typicalRequests.map((item, idx) => (
                          <p key={idx} className="text-sm text-stone-600 italic">
                            "{item}"
                          </p>
                        ))}
                      </div>
                    </div>

                    <Link
                      to="/contact"
                      data-testid={`service-cta-${service.id}`}
                      className="btn-primary inline-flex items-center gap-2"
                    >
                      {t.servicesPage.requestConsultation}
                      <ArrowRight size={16} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-navy">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="font-heading text-3xl md:text-4xl text-white tracking-tight mb-6">
              {t.servicesPage.ctaTitle}
            </h2>
            <p className="text-white/70 leading-relaxed mb-10">
              {t.servicesPage.ctaDesc}
            </p>
            <Link
              to="/contact"
              data-testid="services-cta-btn"
              className="bg-white text-navy px-8 py-4 text-xs font-medium uppercase tracking-widest hover:bg-stone-100 transition-all inline-flex items-center gap-2"
            >
              {t.servicesPage.getInTouch}
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
